import React, { useEffect } from 'react';

export default function InsightDetailModal({ insight, onClose, onScheduleClick }) {
  useEffect(() => {
    if (!insight) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [insight, onClose]);

  if (!insight) return null;

  const { category, readTime, title, image, content } = insight;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-5 lg:p-8">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-gray-950/75 backdrop-blur-md transition-opacity"
        onClick={onClose}
      />

      {/* Modal Card */}
      <div className="relative z-10 w-full max-w-3xl max-h-[92vh] overflow-y-auto bg-white dark:bg-clinical-slate-900 rounded-3xl border border-gray-200/90 dark:border-gray-800 shadow-[0_32px_72px_-12px_rgba(0,0,0,0.55)] flex flex-col">
        {/* Floating Close Button */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close article"
          className="absolute top-4 right-4 z-30 w-10 h-10 rounded-full bg-gray-100/90 dark:bg-clinical-slate-800/90 hover:bg-primary hover:text-white dark:hover:bg-primary text-gray-700 dark:text-gray-200 flex items-center justify-center transition-all shadow-md backdrop-blur-md"
        >
          <span className="material-symbols-outlined text-[20px]">close</span>
        </button>

        {/* Article Header Image & Banner */}
        <div className="relative w-full h-56 sm:h-72 overflow-hidden bg-gray-900 shrink-0">
          <img
            src={image}
            alt={title}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-950/80 via-gray-950/30 to-transparent" />
          
          <div className="absolute bottom-4 left-6 right-12 text-white">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/80 backdrop-blur-md text-white text-xs font-bold uppercase tracking-wider mb-2">
              <span>{category}</span>
              <span>•</span>
              <span>{readTime}</span>
            </div>
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-extrabold tracking-tight leading-tight text-white drop-shadow-sm">
              {title}
            </h2>
          </div>
        </div>

        {/* Article Body */}
        <div className="p-6 sm:p-8 lg:p-10 space-y-6">
          {/* Paragraphs */}
          {content.paragraphs && content.paragraphs.map((p, idx) => (
            <p key={idx} className="text-sm sm:text-base text-gray-700 dark:text-slate-200 leading-relaxed">
              {p}
            </p>
          ))}

          {/* Bulleted Points */}
          {content.bullets && content.bullets.length > 0 && (
            <div className="space-y-4 my-6 bg-surface-container-low/50 dark:bg-clinical-slate-800/50 p-6 rounded-2xl border border-gray-200/80 dark:border-gray-700/70">
              <span className="text-xs font-bold uppercase tracking-wider text-primary dark:text-primary-fixed block mb-2">
                Strategic Imperatives
              </span>
              <ul className="space-y-4">
                {content.bullets.map((b, bIdx) => (
                  <li key={bIdx} className="text-xs sm:text-sm text-gray-700 dark:text-slate-300 leading-relaxed">
                    <strong className="text-gray-900 dark:text-white font-bold block mb-1">
                      {bIdx + 1}. {b.title}
                    </strong>
                    {b.desc}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Conclusion Box */}
          {content.conclusionTitle && (
            <div className="p-5 rounded-2xl bg-blue-50/70 dark:bg-clinical-slate-800/80 border-l-4 border-primary border-t border-r border-b border-blue-100 dark:border-gray-700/80 space-y-2">
              <h4 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white">
                {content.conclusionTitle}
              </h4>
              <p className="text-xs sm:text-sm text-gray-700 dark:text-slate-300 leading-relaxed">
                {content.conclusionText}
              </p>
            </div>
          )}

          {/* Footnote */}
          {content.footnote && (
            <p className="text-xs sm:text-sm italic text-gray-500 dark:text-slate-400 border-t border-gray-200/70 dark:border-gray-800 pt-4">
              {content.footnote}
            </p>
          )}

          {/* Bottom Executive CTA Bar */}
          <div className="pt-4 border-t border-gray-200/80 dark:border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-center sm:text-left">
              <span className="text-xs font-bold uppercase tracking-wider text-primary dark:text-primary-fixed block">
                Executive Advisory
              </span>
              <p className="text-xs text-gray-600 dark:text-slate-300 mt-0.5">
                Discuss this framework with our senior healthcare leadership advisors.
              </p>
            </div>

            <button
              type="button"
              onClick={() => {
                onClose();
                if (onScheduleClick) onScheduleClick();
              }}
              className="shrink-0 inline-flex items-center justify-center gap-2 py-3 px-6 rounded-full bg-primary hover:bg-primary-container text-white font-semibold text-xs sm:text-sm shadow-md hover:shadow-lg transition-all"
            >
              <span>Schedule Briefing</span>
              <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
